import React, { useContext } from 'react';
import {
  bool, node, oneOf, shape, string, arrayOf
} from 'prop-types';
import classNames from 'classnames/bind';
import { Placeholder } from '@thd-olt-component-react/core-ui';
import { StorePodContext } from '../../StorePod.context';
import styles from './store-pod-name.module.scss';

const cx = classNames.bind(styles);

export const StorePodName = ({
  allowOverflow,
  isHeader,
  markup,
  className,
  font,
  hideStoreNumber,
  label,
  linkType,
}) => {
  const { loading, store } = useContext(StorePodContext);

  const { name, storeId, storeDetailsPageLink } = store;

  const fontMerged = { ...StorePodName.defaultProps.font, ...font };

  const sizeMap = {
    small: {
      fontSize: '18px',
      height: '22px'
    },
    medium: {
      fontSize: '24px',
      height: '28px'
    },
    large: {
      fontSize: '30px',
      height: '36px'
    },
    xlarge: {
      fontSize: '36px',
      height: '43px'
    }
  };

  const { size, weight } = fontMerged || {};
  let { height, fontSize } = sizeMap[size];
  if (weight === 'light') {
    let heightInt = height.slice(0, -2);
    heightInt -= 1;
    height = heightInt + 'px';
  }

  const nameClasses = cx(className, 'store-pod-name', {
    'store-pod-name--bold': weight === 'bold',
    'store-pod-name--light': weight === 'light'
  });

  const wrapperClasses = cx('store-pod-name__wrapper', {
    'store-pod-name__wrapper--no-overflow': !allowOverflow
  });

  if (loading) {
    return (
      <div className={nameClasses}>
        <Placeholder height={height} type="rect" width="100%" />
      </div>
    );
  }

  // eslint-disable-next-line react/prop-types
  const StorePodNameWrapper = ({ children }) => {
    let elementType = markup;
    /** will be depreciated: use prop "markup" instead */
    if (isHeader) elementType = 'h1';

    return React.createElement(elementType, {
      className: wrapperClasses,
      'data-testid': 'store-pod-name',
      style: { fontSize }
    }, children);
  };

  const linkMap = {
    rentals: {
      href: `${storeDetailsPageLink}/rentals`,
    },
    services: {
      href: `${storeDetailsPageLink}/services`,
    },
    store: {
      href: storeDetailsPageLink,
    }
  };

  const { href } = linkMap[linkType];

  return (
    <a
      className={nameClasses}
      href={href}
    >
      <StorePodNameWrapper>
        {label && (
          <span>{label}</span>
        )}
        {name}
        {!hideStoreNumber && (
          <span>&nbsp;#{storeId}</span>
        )}
      </StorePodNameWrapper>
    </a>
  );
};

StorePodName.displayName = 'StorePodName';

StorePodName.propTypes = {
  allowOverflow: bool,
  className: string,
  font: shape({
    size: oneOf(['small', 'medium', 'large', 'xlarge']),
    weight: oneOf(['light', 'normal', 'bold'])
  }),
  hideStoreNumber: bool,
  label: string,
  linkType: oneOf(['rentals', 'services', 'store']),
  /** will be depreciated: use prop "markup" instead */
  isHeader: bool,
  /** prefer this over isHeader: sets element type of StorePodName */
  markup: oneOf(['h1', 'h3', 'div']),
};

StorePodName.defaultProps = {
  allowOverflow: false,
  className: null,
  font: {
    size: 'small',
    weight: 'bold'
  },
  hideStoreNumber: false,
  label: '',
  linkType: 'store',
  isHeader: false,
  markup: 'div'
};
